import type { CameraState, Quat } from '../types.ts';

export const QUAT_IDENTITY: Quat = [0, 0, 0, 1];

export function newCamera(): CameraState {
  return { scale: 1, tx: 0, ty: 0, rot: [0, 0, 0, 1] };
}

// ---- quaternions (x, y, z, w), same conventions as the Curve Atlas page ----

export function quatAxisAngle(axis: [number, number, number], angle: number): Quat {
  const s = Math.sin(angle / 2);
  return [axis[0] * s, axis[1] * s, axis[2] * s, Math.cos(angle / 2)];
}

/** a * b: apply b first, then a. */
export function quatMul(a: Quat, b: Quat): Quat {
  return [
    a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
    a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
    a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
    a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
  ];
}

export function quatNormalize(q: Quat): Quat {
  const l = Math.hypot(q[0], q[1], q[2], q[3]) || 1;
  return [q[0] / l, q[1] / l, q[2] / l, q[3] / l];
}

/** Column-major 3x3 rotation matrix (what GLSL `mat3` and `rotateVec` expect). */
export function quatToMat3(q: Quat): Float32Array {
  const [x, y, z, w] = q;
  return new Float32Array([
    1 - 2 * (y * y + z * z), 2 * (x * y + z * w), 2 * (x * z - y * w),
    2 * (x * y - z * w), 1 - 2 * (x * x + z * z), 2 * (y * z + x * w),
    2 * (x * z + y * w), 2 * (y * z - x * w), 1 - 2 * (x * x + y * y),
  ]);
}

/** r = M * v for a column-major matrix. */
export function rotateVec(m: ArrayLike<number>, x: number, y: number, z: number): [number, number, number] {
  return [
    m[0] * x + m[3] * y + m[6] * z,
    m[1] * x + m[4] * y + m[7] * z,
    m[2] * x + m[5] * y + m[8] * z,
  ];
}

/** Quaternion for the legacy two-axis operator `rotatePoint` (X first, then Y); used for old links. */
export function quatFromEuler(rotX: number, rotY: number): Quat {
  return quatNormalize(quatMul(quatAxisAngle([0, 1, 0], rotY), quatAxisAngle([1, 0, 0], rotX)));
}

/** Legacy two-axis rotation used by the original viewer; kept for tests and for decoding old links. */
export function rotatePoint(x: number, y: number, z: number, rotX: number, rotY: number): [number, number, number] {
  const cx = Math.cos(rotX), sx = Math.sin(rotX);
  const cy = Math.cos(rotY), sy = Math.sin(rotY);
  const y1 = y * cx - z * sx;
  const z1 = y * sx + z * cx;
  const z2 = z1 * cy - x * sy;
  const x2 = z1 * sy + x * cy;
  return [x2, y1, z2];
}

// ---- arcball ----

/** Point on the unit arcball for screen position (x, y), given the ball's centre and radius in px. */
export function arcballPoint(x: number, y: number, cx: number, cy: number, r: number): [number, number, number] {
  let a = (x - cx) / r, b = (cy - y) / r;
  const l = a * a + b * b;
  if (l > 1) { const k = 1 / Math.sqrt(l); a *= k; b *= k; return [a, b, 0]; }
  return [a, b, Math.sqrt(1 - l)];
}

/** Rotation that carries arcball point a onto b, composed in front of q (screen-space rotation). */
export function arcballOrbit(q: Quat, x0: number, y0: number, x1: number, y1: number, cx: number, cy: number, r: number): Quat {
  const a = arcballPoint(x0, y0, cx, cy, r), b = arcballPoint(x1, y1, cx, cy, r);
  let cross: [number, number, number] = [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const dot = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  let d: Quat;
  if (dot < -0.999999) {
    cross = Math.abs(a[0]) < 0.8 ? [0, -a[2], a[1]] : [-a[1], a[0], 0];
    const l = Math.hypot(...cross) || 1;
    d = quatAxisAngle([cross[0] / l, cross[1] / l, cross[2] / l], Math.PI);
  } else {
    d = quatNormalize([cross[0], cross[1], cross[2], 1 + dot]);
  }
  return quatNormalize(quatMul(d, q));
}

// ---- fitting and zoom ----

/** Perspective magnification for a normalised depth (camera 4.5 extents from the centre). */
export function perspFactor(depth: number): number {
  const d = Math.max(-3, Math.min(3, depth));
  return 4.5 / (4.5 - d);
}

/** Area-preserving anisotropic stretch diag(k, 1/k), k = 2^aspect. */
export function aspectK(aspect: number): number {
  const k = Math.pow(2, aspect || 0);
  return k > 0 ? k : 1;
}

function bboxCorners(min: number[], max: number[]): [number, number, number][] {
  const out: [number, number, number][] = [];
  for (const x of [min[0], max[0]]) for (const y of [min[1], max[1]]) for (const z of [min[2], max[2]]) out.push([x, y, z]);
  return out;
}

/**
 * Fit the bbox into the viewport with 8% padding. Mutates cam scale/tx/ty.
 * In 3D the rotation pivots on the bbox centre (screen = R (p - c) scale + t),
 * so the corners are taken relative to it.
 */
export function fitToView(
  cam: CameraState,
  bbox: { min: number[]; max: number[] },
  viewW: number,
  viewH: number,
  k: number,
  is3d: boolean,
  perspExtent = 0,   // > 0: perspective with the camera 4.5 extents away (see project() in the shaders)
): void {
  let min2: number[], max2: number[];
  if (is3d) {
    const m = quatToMat3(cam.rot);
    const cc = [0, 1, 2].map((i) => ((bbox.min[i] ?? 0) + (bbox.max[i] ?? 0)) / 2);
    min2 = [Infinity, Infinity];
    max2 = [-Infinity, -Infinity];
    for (const c of bboxCorners(bbox.min, bbox.max)) {
      const r = rotateVec(m, c[0] - cc[0], c[1] - cc[1], c[2] - cc[2]);
      const f = perspExtent > 0 ? perspFactor(r[2] / perspExtent) : 1;
      min2[0] = Math.min(min2[0], r[0] * f); min2[1] = Math.min(min2[1], r[1] * f);
      max2[0] = Math.max(max2[0], r[0] * f); max2[1] = Math.max(max2[1], r[1] * f);
    }
  } else {
    min2 = [bbox.min[0] * k, bbox.min[1] / k];
    max2 = [bbox.max[0] * k, bbox.max[1] / k];
  }
  const pad = 0.08;
  const spanX = Math.max(1e-9, max2[0] - min2[0]);
  const spanY = Math.max(1e-9, max2[1] - min2[1]);
  cam.scale = (1 - pad * 2) * Math.min(viewW / spanX, viewH / spanY);
  cam.tx = viewW * 0.5 - (min2[0] + max2[0]) * 0.5 * cam.scale;
  cam.ty = viewH * 0.5 + (min2[1] + max2[1]) * 0.5 * cam.scale;
}

export function resetCamera(cam: CameraState): void {
  cam.rot = [0, 0, 0, 1]; cam.scale = 1; cam.tx = 0; cam.ty = 0;
}

/** Zoom by factor keeping the point (px, py) fixed on screen. */
export function zoomAt(cam: CameraState, px: number, py: number, factor: number): void {
  cam.tx = px - (px - cam.tx) * factor;
  cam.ty = py - (py - cam.ty) * factor;
  cam.scale *= factor;
}
